import { graphql } from "babel-plugin-relay/macro";
import { useFragment, useLazyLoadQuery, useMutation } from "react-relay";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { NavigateDirectToQuestionsAdvanceTreeButton_LearnOpportunityV2Fragment$key } from "@generated/NavigateDirectToQuestionsAdvanceTreeButton_LearnOpportunityV2Fragment.graphql";
import { NavigateDirectToQuestionsAdvanceTreeButton_Query } from "@generated/NavigateDirectToQuestionsAdvanceTreeButton_Query.graphql";
import { NavigateDirectToQuestionsAdvanceTreeButton_StartContentNodeMutation } from "@generated/NavigateDirectToQuestionsAdvanceTreeButton_StartContentNodeMutation.graphql";
import { NavigateDirectToQuestionsAdvanceTreeButton_StartTreeMutation } from "@generated/NavigateDirectToQuestionsAdvanceTreeButton_StartTreeMutation.graphql";
import { NavigateDirectToQuestionsAdvanceTreeButton_UserFragment$key } from "@generated/NavigateDirectToQuestionsAdvanceTreeButton_UserFragment.graphql";
import { NavigationButtonLabel } from "./left-frame/NavigationButtonLabel";
import { NavigateDirectToQuestionsAdvanceContentButton } from "./NavigateDirectToQuestionsAdvanceContentButton";
import { PrimaryButton } from "../../infecto-lms-webapp/components/buttons/PrimaryButton";
import { selectTooltipStartConfigType } from "../../infecto-lms-webapp/utils/selectTooltipStartConfigType";
import { DASHBOARD_PATH } from "../../infecto-lms-webapp-impl/router/routes/auth/dashboard.routes";
import { elementTypeToNextButtonText } from "../utils/elementTypeToNextButtonText";
import { selectCorrectPathToContent } from "../utils/selectCorrectPathToContent";

const TREE_ADVANCEMENT_RESULT_FRAGMENT = graphql`
	fragment NavigateDirectToQuestionsAdvanceTreeButton_LearnOpportunityV2Fragment on LearnOpportunityV2 {
		id
		shortDescription
		root {
			id
			structureDefinition {
				title
				... on LearnOpportunityRootStructureDefinition {
					viewerTreeState {
						kind

						... on CanNotBeStartedViewerTreeState {
							reasonConfigTypes
						}
						... on IsStartedViewerTreeState {
							headContentId
						}
					}
				}
			}
		}
		structureDefinition {
			title
		}
		typeDefinition {
			definitionType
			... on LearnOpportunityELearningContentTypeDefinition {
				contentNodeAdvancementResult {
					status
				}
				containedElementTypes
				extension {
					... on LearnContentExtensionImpl {
						cmePoints
						headFirstElementType
					}
				}
			}
		}
	}
`;

const USER_QUERY = graphql`
	query NavigateDirectToQuestionsAdvanceTreeButton_Query {
		Viewer {
			Auth {
				currentUser {
					user {
						...NavigateDirectToQuestionsAdvanceTreeButton_UserFragment
					}
				}
			}
		}
	}
`;

const USER_FRAGMENT = graphql`
	fragment NavigateDirectToQuestionsAdvanceTreeButton_UserFragment on User {
		extension(userExtensionKind: InfectopharmUserExtension) {
			... on InfectopharmUserExtension {
				globalUserStatus
			}
		}
	}
`;

const START_TREE_MUTATION = graphql`
	mutation NavigateDirectToQuestionsAdvanceTreeButton_StartTreeMutation($input: StartTreeInput!) {
		LearnV2 {
			startTree(input: $input) {
				learnOpportunity {
					id
					structureDefinition {
						... on LearnOpportunityRootStructureDefinition {
							viewerTreeState {
								kind
								... on IsStartedViewerTreeState {
									headContentId
								}
							}
						}
					}
					typeDefinition {
						... on LearnOpportunityELearningContentTypeDefinition {
							contentNodeAdvancementResult {
								status
							}
						}
					}
					...NodeScreen_NodeFragment
				}
			}
		}
	}
`;

const START_CONTENT_NODE_MUTATION = graphql`
	mutation NavigateDirectToQuestionsAdvanceTreeButton_StartContentNodeMutation(
		$input: StartContentNodeInput!
	) {
		LearnV2 {
			startContentNode(input: $input) {
				contentSubmission {
					id
				}
			}
		}
	}
`;

interface OwnProps {
	learnOpportunityFragmentRef: NavigateDirectToQuestionsAdvanceTreeButton_LearnOpportunityV2Fragment$key;
	showFinished: boolean;
	className?: string;
}

export function NavigateDirectToQuestionsAdvanceTreeButton({
	learnOpportunityFragmentRef,
	showFinished,
	className,
}: OwnProps) {
	const navigate = useNavigate();

	const learnOpportunity =
		useFragment<NavigateDirectToQuestionsAdvanceTreeButton_LearnOpportunityV2Fragment$key>(
			TREE_ADVANCEMENT_RESULT_FRAGMENT,
			learnOpportunityFragmentRef,
		);
	const userQuery = useLazyLoadQuery<NavigateDirectToQuestionsAdvanceTreeButton_Query>(
		USER_QUERY,
		{},
	);
	const user = useFragment<NavigateDirectToQuestionsAdvanceTreeButton_UserFragment$key>(
		USER_FRAGMENT,
		userQuery?.Viewer.Auth.currentUser?.user!, //eslint-disable-line @typescript-eslint/no-non-null-asserted-optional-chain
	);
	const [startTree, isStartingTree] =
		useMutation<NavigateDirectToQuestionsAdvanceTreeButton_StartTreeMutation>(START_TREE_MUTATION);

	const [startContentNode, isStartingContentNode] =
		useMutation<NavigateDirectToQuestionsAdvanceTreeButton_StartContentNodeMutation>(
			START_CONTENT_NODE_MUTATION,
		);

	const isNoMultipleChoiceElement =
		!learnOpportunity?.typeDefinition.containedElementTypes?.includes("multipleChoice");

	return (
		<div>
			{(() => {
				switch (learnOpportunity.root?.structureDefinition.viewerTreeState?.kind) {
					case "CanBeStarted":
						const startAtContentNodeId =
							learnOpportunity.typeDefinition.definitionType === "content" &&
							(learnOpportunity.typeDefinition.extension?.cmePoints || -1) > 0
								? learnOpportunity.id
								: null;
						return (
							<LinkButton
								disabled={isStartingTree || isStartingContentNode}
								className={`bg-primary ${className}`}
								onClick={() => {
									startTree({
										variables: {
											input: {
												nodeId: learnOpportunity.id,
												startAtContentNodeId: startAtContentNodeId,
											},
										},
										onCompleted: (startTreeResponse) => {
											if (
												startTreeResponse.LearnV2.startTree?.learnOpportunity.typeDefinition
													.contentNodeAdvancementResult?.status === "CanBeStarted"
											) {
												startContentNode({
													variables: {
														input: {
															contentNodeId: learnOpportunity.id,
														},
													},
													onCompleted: () => {
														navigate(
															selectCorrectPathToContent(
																learnOpportunity?.root?.id,
																learnOpportunity?.id!, //eslint-disable-line @typescript-eslint/no-non-null-asserted-optional-chain
																isNoMultipleChoiceElement,
															),
														);
													},
												});
												return;
											}
										},
									});
								}}
							>
								<NavigationButtonLabel
									elementTypeLabel={elementTypeToNextButtonText(
										learnOpportunity.typeDefinition.containedElementTypes,
									)}
									title={learnOpportunity.structureDefinition.title}
									shortDescription={learnOpportunity.shortDescription}
								/>
							</LinkButton>
						);
					case "CanNotBeStarted":
						return (
							<LinkButton
								disabled
								className={`bg-primary ${className}`}
								tooltipOptions={{
									showOnDisabled: true,
									position: "bottom",
									mouseTrack: true,
									mouseTrackTop: 0,
								}}
								tooltip={selectTooltipStartConfigType(
									learnOpportunity.root.structureDefinition.viewerTreeState.reasonConfigTypes,
									user.extension?.globalUserStatus,
								)}
							>
								{" "}
								<NavigationButtonLabel
									elementTypeLabel={elementTypeToNextButtonText(
										learnOpportunity.typeDefinition.containedElementTypes,
									)}
									title={learnOpportunity.structureDefinition.title}
									shortDescription={learnOpportunity.shortDescription}
								/>
							</LinkButton>
						);
					case "IsStarted":
						switch (learnOpportunity.typeDefinition.contentNodeAdvancementResult?.status) {
							case "CanBeStarted":
							case "CanBeRestartedAfterPassed":
							case "BlockedByTree":
								return (
									<NavigateDirectToQuestionsAdvanceContentButton
										learnOpportunityId={learnOpportunity.id}
										className={"bg-primary ml-0 md:ml-3 px-4 py-3"}
									/>
								);
							case "CanBeRestartedAfterFailed":
								break;
							default:
								break;
						}
						break;
					case "IsFinished":
						return (
							showFinished && (
								<LinkButton
									className="p-button-primary bg-primary border-primary"
									onClick={() => navigate(DASHBOARD_PATH)}
									label="Modul abgeschlossen"
								/>
							)
						);
					default:
						// NotVisible, AtHead, NotAtHead, isFinished
						return null;
				}
			})()}
		</div>
	);
}

const LinkButton = styled(PrimaryButton)`
	display: flex;
	width: 100%;
	justify-content: center;
	text-align: center;
	border: none;
	cursor: pointer;
`;
